import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Button, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import {
  ageDateToInput,
  agePlainDateToDate,
  deriveAge,
  formatAgeDate,
  parseAgeInputDate,
  type AgeResult,
} from '@/pages/age-calculator/model/ageCalculator'

const UTILITY = 'age-calculator'

function formatCalendarMonth(date: Date | null, locale: string): string {
  if (!date) return '---'
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
}

function formatCalendarDay(date: Date | null): string {
  if (!date) return '--'
  return String(date.getDate()).padStart(2, '0')
}

export function AgeCalculatorPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const today = useMemo(() => new Date(), [])
  const todayInput = useMemo(() => ageDateToInput(today), [today])
  const [birthDate, setBirthDate] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [birthBlurred, setBirthBlurred] = useState(false)
  const [targetBlurred, setTargetBlurred] = useState(false)

  const birth = useMemo(() => parseAgeInputDate(birthDate), [birthDate])
  const target = useMemo(
    () => parseAgeInputDate(targetDate || todayInput),
    [targetDate, todayInput],
  )
  const birthAsDate = birth ? agePlainDateToDate(birth) : null
  const targetAsDate = target ? agePlainDateToDate(target) : null
  const { result, error } = useMemo(
    () => deriveAge(
      birthDate,
      targetDate,
      todayInput,
      { birthBlurred, targetBlurred },
    ),
    [birthBlurred, birthDate, targetBlurred, targetDate, todayInput],
  )

  const handleToday = useCallback(() => {
    setTargetDate('')
    setTargetBlurred(false)
    toolInvoke(UTILITY, { action: 'today' })
  }, [])

  const handleClear = useCallback(() => {
    setBirthDate('')
    setTargetDate('')
    setBirthBlurred(false)
    setTargetBlurred(false)
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.ageCalculator.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.ageCalculator.description}
        </p>
      </section>

      <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-emerald-400 via-primary to-cyan-400"
        />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
          <div className="space-y-5 border-b p-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid gap-4 sm:grid-cols-2">
              <DateField
                id="birth-date"
                label={t.ageCalculator.birthDate}
                value={birthDate}
                date={birthAsDate}
                locale={localeName}
                max={todayInput}
                onChange={(value) => {
                  setBirthDate(value)
                }}
                onBlur={() => setBirthBlurred(true)}
                invalid={error === 'missing_birth_date' || error === 'invalid_birth_date' || error === 'future_birth_date'}
              />
              <DateField
                id="target-date"
                label={t.ageCalculator.targetDate}
                hint={t.ageCalculator.targetDateHint}
                value={targetDate}
                date={targetAsDate}
                locale={localeName}
                max={todayInput}
                onChange={(value) => {
                  setTargetDate(value)
                }}
                onBlur={() => setTargetBlurred(true)}
                invalid={error === 'invalid_target_date' || error === 'future_target_date' || error === 'target_before_birth'}
              />
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {t.ageCalculator.errors[error]}
              </div>
            )}

            <div className="rounded-lg border bg-background/70 p-4 shadow-inner">
              <h2 className="text-sm font-semibold tracking-normal">
                {t.ageCalculator.hintTitle}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t.ageCalculator.hintDescription}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" onClick={handleToday} disabled={!targetDate}>
                {t.ageCalculator.useToday}
              </Button>
              <Button variant="ghost" onClick={handleClear}>
                {t.common.clear}
              </Button>
            </div>
          </div>

          <ResultPanel result={result} locale={localeName} />
        </div>
      </section>

      <RelatedLinks />

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.ageCalculator.faqTitle}</h2>
        <dl className="mt-4 space-y-4">
          {t.ageCalculator.faq.map((item) => (
            <FaqItem key={item.question} q={item.question} a={item.answer} />
          ))}
        </dl>
      </section>
    </div>
  )
}

function DateField({
  id,
  label,
  hint,
  value,
  date,
  locale,
  max,
  onChange,
  onBlur,
  invalid = false,
}: {
  id: string
  label: string
  hint?: string
  value: string
  date: Date | null
  locale: string
  max: string
  onChange: (value: string) => void
  onBlur: () => void
  invalid?: boolean
}) {
  return (
    <label
      htmlFor={id}
      className="block rounded-lg border bg-background/70 p-3 shadow-inner transition-colors focus-within:border-primary/50"
    >
      <span className="mb-2 block text-sm font-medium">{label}</span>
      {hint && (
        <span className="mb-3 block text-xs leading-5 text-muted-foreground">{hint}</span>
      )}
      <div className="grid gap-3 sm:grid-cols-[80px_minmax(0,1fr)] sm:items-center">
        <CalendarTile date={date} locale={locale} />
        <input
          id={id}
          type="date"
          value={value}
          max={max}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-invalid={invalid}
          className="h-11 w-full min-w-0 rounded-md border border-input bg-card px-3 pr-10 text-base shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring sm:h-10 sm:text-sm"
        />
      </div>
    </label>
  )
}

function CalendarTile({ date, locale }: { date: Date | null; locale: string }) {
  return (
    <span className="overflow-hidden rounded-md border bg-card text-center shadow-sm">
      <span className="block bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">
        {formatCalendarMonth(date, locale)}
      </span>
      <span className="block px-3 py-2 text-2xl font-bold leading-none tabular-nums">
        {formatCalendarDay(date)}
      </span>
    </span>
  )
}

function ResultPanel({ result, locale }: { result: AgeResult | null; locale: string }) {
  const t = useT()

  if (!result) {
    return (
      <div className="flex min-h-[360px] flex-col justify-between gap-6 bg-secondary/30 p-5 sm:p-6">
        <div>
          <p className="text-sm font-semibold text-primary">{t.ageCalculator.resultReady}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">
            {t.ageCalculator.emptyResultTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.ageCalculator.emptyResultDescription}
          </p>
        </div>
        <div className="rounded-lg border bg-card/80 p-4 shadow-sm">
          <div className="grid grid-cols-3 gap-2">
            <Metric label={t.ageCalculator.years} value="--" />
            <Metric label={t.ageCalculator.months} value="--" />
            <Metric label={t.ageCalculator.days} value="--" />
          </div>
          <div className="mt-4 h-3 rounded-full bg-muted" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 bg-secondary/30 p-5 sm:p-6">
      <div>
        <p className="text-sm font-semibold text-primary">{t.ageCalculator.resultReady}</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Metric label={t.ageCalculator.years} value={result.years} accent />
          <Metric label={t.ageCalculator.months} value={result.months} />
          <Metric label={t.ageCalculator.days} value={result.days} />
        </div>
      </div>

      <div className="rounded-lg border bg-card/80 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium">{t.ageCalculator.period}</span>
          <span className="text-xs text-muted-foreground">
            {formatAgeDate(result.birthDate, locale)} - {formatAgeDate(result.targetDate, locale)}
          </span>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <Metric label={t.ageCalculator.totalDays} value={result.totalDays} accent />
          <Metric label={t.ageCalculator.nextBirthday} value={result.daysToNextBirthday} />
        </div>
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        {t.ageCalculator.nextBirthdayHint.replace(
          '{date}',
          formatAgeDate(result.nextBirthday, locale),
        )}
      </p>
    </div>
  )
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string | number
  accent?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        accent ? 'bg-accent text-accent-foreground' : 'bg-background/70',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}

function RelatedLinks() {
  const t = useT()

  return (
    <section className="mt-10 border-t pt-8">
      <h2 className="text-xl font-semibold tracking-normal">{t.ageCalculator.relatedTitle}</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {t.ageCalculator.related.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="rounded-lg border bg-card px-4 py-3 text-sm shadow-sm transition-colors hover:border-primary/40 hover:bg-accent"
          >
            <span className="font-medium">{item.title}</span>
            <span className="mt-1 block leading-5 text-muted-foreground">{item.description}</span>
          </a>
        ))}
      </div>
    </section>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <dt className="font-medium">{q}</dt>
      <dd className="mt-1 text-sm leading-6 text-muted-foreground">{a}</dd>
    </div>
  )
}
